import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuthContext } from 'src/auth/hooks';
import DocumentWorkflow from '@heyjob-dash/shared/company/documents/DocumentWorkflow';

import { Box, Card, Chip, Alert, Stack, Button, Container, Typography, CardContent } from '@mui/material';

import OnboardingService from '../services/OnboardingService';
import { useOnboardingCompanySync } from './use-onboarding-company-sync';
import DocumentWorkflowService from '../services/DocumentWorkflowService';
import OnboardingDocuments from '../sections/onboarding/OnboardingDocuments';
import StudioDocumentsToSign from '../sections/onboarding/StudioDocumentsToSign';
import OnboardingSteps, { ONBOARDING_STEPS } from '../sections/onboarding/OnboardingSteps';

export default function CompanyOnboardingPage() {
  const { companyId } = useParams();
  const { user, selectCompany, fetchUserData } = useAuthContext();
  const companies = user?.data?.companies || {};
  const company = companies[companyId];
  const [collection, setCollection] = useState(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [step, setStep] = useState('documents');
  const [documentSummary, setDocumentSummary] = useState({ accepted: 0, total: 0 });
  const onSummaryChange = useCallback((summary) => setDocumentSummary(summary), []);
  const [delegations, setDelegations] = useState([]);
  const authorized = Boolean(user?.groups?.includes('Azienda') && company);
  useEffect(() => {
    let current = true;
    setCollection(null);
    setError(false);
    if (authorized) {
      OnboardingService.collection(companyId).then((result) => {
        if (current) { setCollection(result); if (result.company.status === 'active') setStep('activation'); }
        return OnboardingService.access(companyId);
      }).catch(() => { if (current) setError(true); });
    }
    return () => { current = false; };
  }, [companyId, authorized, attempt]);
  useEffect(() => { setStep('documents'); setDocumentSummary({ accepted: 0, total: 0 }); setDelegations([]); }, [companyId]);
  useOnboardingCompanySync(companyId);
  const active = collection?.company.status === 'active';
  const toSign = delegations.filter((item) => item.status === 'company_to_complete' || item.status === 'company_to_sign').length;
  let delegationCaption = 'Nessuno da firmare';
  if (toSign) delegationCaption = `${toSign} da firmare`;
  else if (delegations.some((item) => item.status === 'studio_to_sign')) delegationCaption = 'In attesa dello studio';
  else if (delegations.length) delegationCaption = 'Firmati';
  const stepStatus = {
    documents: { done: documentSummary.total > 0 && documentSummary.accepted === documentSummary.total, caption: documentSummary.total ? `${documentSummary.accepted} di ${documentSummary.total} accettati` : 'Facoltativi' },
    delegations: { done: delegations.length > 0 && delegations.every((item) => item.status === 'signed'), caption: delegationCaption, attention: toSign > 0 },
    activation: { done: active, caption: active ? 'Conclusa' : 'A cura di Studio' },
  };
  const stepIndex = ONBOARDING_STEPS.findIndex((item) => item.key === step);
  const previousStep = ONBOARDING_STEPS[stepIndex - 1];
  const nextStep = ONBOARDING_STEPS[stepIndex + 1];
  if (!authorized) return <Container sx={{ py: 6 }}><Alert severity="warning" action={<Button color="inherit" href="/">Torna a HeyJob</Button>}>Questo account non ha accesso all’onboarding richiesto.</Alert></Container>;
  return (
    <Container maxWidth="md" sx={{ maxWidth: '800px !important', py: { xs: 1, md: 3 } }}>
      <Stack spacing={4}>
        <Stack spacing={1.5}>
          <Box><Chip label="Onboarding azienda" color="primary" variant="soft" size="small" /></Box>
          <Typography variant="h4" component="h1">Benvenuto, {company.name}</Typography>
          <Typography variant="body2" color="text.secondary">Mettiamo a punto la tua azienda su HeyJob. Segui le fasi qui sotto: carica i documenti disponibili e firma i documenti che Studio ti invia. Le altre sezioni di HeyJob si attiveranno quando Studio concluderà l’onboarding.</Typography>
        </Stack>
        {error && <Alert severity="error" action={<Button onClick={() => setAttempt((value) => value + 1)}>Riprova</Button>}>Non è stato possibile aggiornare i documenti.</Alert>}
        {!collection && !error && <Typography>Caricamento documenti…</Typography>}
        {collection && <Card><CardContent sx={{ py: 2.5, '&:last-child': { pb: 2.5 } }}><OnboardingSteps active={step} status={stepStatus} onSelect={setStep} /></CardContent></Card>}
        {collection?.company.status === 'onboarding' && <Box sx={{ display: step === 'documents' ? 'block' : 'none' }}><Stack spacing={2.5}><DocumentWorkflow key={`requests-${companyId}`} companyId={companyId} scope="onboarding" service={DocumentWorkflowService} onSummaryChange={onSummaryChange} /><OnboardingDocuments key={companyId} documents={collection.documents} onDownload={async (id) => { const { url } = await OnboardingService.download(companyId, id); window.open(url, '_blank', 'noopener,noreferrer'); }} /></Stack></Box>}
        {collection && <Box sx={{ display: step === 'delegations' ? 'block' : 'none' }}><StudioDocumentsToSign key={`delegations-${companyId}`} companyId={companyId} variant="onboarding" onItemsChange={setDelegations} /></Box>}
        {collection && step === 'activation' && (active
          ? <Alert severity="success" action={<Button onClick={async () => { try { await fetchUserData(); selectCompany(companyId); window.location.assign('/'); } catch { setError(true); } }}>Accedi</Button>}>Studio ha concluso l’onboarding. Puoi accedere a HeyJob.</Alert>
          : <Stack spacing={0.5}><Typography variant="h6" component="h2">Attivazione</Typography><Typography variant="body2" color="text.secondary">Studio completa la scheda della tua azienda con i documenti e i dati ricevuti. Quando Studio concluderà l’onboarding, potrai accedere a tutte le sezioni di HeyJob da questa pagina.</Typography></Stack>)}
        {collection && <Stack direction="row" justifyContent="space-between">
          {previousStep ? <Button color="inherit" onClick={() => setStep(previousStep.key)}>‹ {previousStep.label}</Button> : <span />}
          {nextStep && <Button variant="outlined" onClick={() => setStep(nextStep.key)}>{nextStep.label} ›</Button>}
        </Stack>}
      </Stack>
    </Container>
  );
}
